import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  BarChart2,
  Target,
  TrendingUp,
  Send,
  Settings,
  LogOut,
  RefreshCw,
  Database,
  Plus,
  Zap,
  BookOpen,
  CheckCircle,
  Loader2,
  Building,
  UserPlus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Activity,
  Shield,
  ChevronLeft,
  ChevronRight,
  Award,
  X,
  Search,
  Swords,
} from "lucide-react";
import { motion } from "framer-motion";

// ─── Style constants ─────────────────────────────────────────────────────────
const bg       = "#0a0f1a";
const surface  = "#111827";
const surface2 = "#1f2937";
const border   = "#374151";
const textPri  = "#f9fafb";
const textSec  = "#9ca3af";
const green    = "#10b981";
const rose     = "#ef4444";
const amber    = "#f59e0b";
const blue     = "#3b82f6";
const purple   = "#8b5cf6";

const inputStyle = {
  width: "100%",
  background: bg,
  border: `1px solid ${border}`,
  color: textPri,
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Nunito, sans-serif",
};

const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
};

const labelStyle = {
  display: "block",
  marginBottom: 6,
  fontSize: 13,
  color: textSec,
  fontFamily: "Nunito, sans-serif",
};

const btnPrimary = {
  background: "linear-gradient(135deg, #10b981, #059669)",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "10px 20px",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontFamily: "Nunito, sans-serif",
};

const btnDanger = {
  ...btnPrimary,
  background: "linear-gradient(135deg, #ef4444, #dc2626)",
};

const btnSecondary = {
  background: surface2,
  color: textSec,
  border: `1px solid ${border}`,
  borderRadius: 10,
  padding: "10px 20px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontFamily: "Nunito, sans-serif",
};

const btnIcon = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: "6px 8px",
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const cardStyle = {
  background: surface,
  border: `1px solid ${border}`,
  borderRadius: 16,
  padding: 20,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
  fontFamily: "Nunito, sans-serif",
};

const thStyle = {
  textAlign: "left",
  padding: "12px 16px",
  color: textSec,
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: `1px solid ${border}`,
  background: surface2,
};

const tdStyle = {
  padding: "12px 16px",
  color: textPri,
  borderBottom: `1px solid ${border}`,
  verticalAlign: "middle",
};

// ─── Modal wrapper ────────────────────────────────────────────────────────────
const Modal = ({ open, onClose, title, children, maxWidth = 520 }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          background: surface2,
          borderRadius: 20,
          padding: 32,
          width: "90%",
          maxWidth,
          maxHeight: "90vh",
          overflow: "auto",
          position: "relative",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ color: textPri, fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 18, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ ...btnIcon, color: textSec }}>
            <X size={18} />
          </button>
        </div>
        {children}
      </motion.div>
    </div>
  );
};

// ─── Progress bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, max, color = green }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ background: surface2, borderRadius: 999, height: 6, overflow: "hidden", width: "100%" }}>
      <div style={{ width: `${pct}%`, background: color, height: "100%", borderRadius: 999, transition: "width 0.4s ease" }} />
    </div>
  );
};

// ─── Avatar initial ───────────────────────────────────────────────────────────
const Avatar = ({ name = "", size = 36 }) => {
  const colors = [blue, purple, green, amber, rose];
  const idx = name.charCodeAt(0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: colors[idx] + "33",
      border: `2px solid ${colors[idx]}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, fontWeight: 800, color: colors[idx],
      flexShrink: 0,
      fontFamily: "Nunito, sans-serif",
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

// ─── Difficulty badge ─────────────────────────────────────────────────────────
const DIFF_LABEL = { easy: "Basic", medium: "Moderate", hard: "Advanced", expert: "Expert", super_hard: "Super Hard" };
const DiffBadge = ({ diff }) => {
  const map = { easy: [green, "#052e16"], medium: [amber, "#2d1a00"], hard: [rose, "#2d0505"], expert: ["#7c3aed", "#1e1040"], super_hard: ["#a855f7", "#1a0030"] };
  const [c, bg2] = map[diff] || [textSec, surface2];
  return (
    <span style={{ background: bg2, color: c, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
      {DIFF_LABEL[diff] || diff}
    </span>
  );
};

// ─── KPI card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
  <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 16 }}>
    <div style={{ width: 48, height: 48, borderRadius: 14, background: color + "22", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon size={22} color={color} />
    </div>
    <div>
      <div style={{ color: textSec, fontSize: 12, marginBottom: 2, fontFamily: "Nunito, sans-serif" }}>{label}</div>
      <div style={{ color: textPri, fontSize: 24, fontWeight: 800, fontFamily: "Nunito, sans-serif", lineHeight: 1.1 }}>{value ?? "—"}</div>
      {sub && <div style={{ color: green, fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  </div>
);

// ─── CSS bar chart ─────────────────────────────────────────────────────────────
const BarChart = ({ data, colorMap }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-end", height: 140 }}>
      {data.map((d) => (
        <div key={d.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <span style={{ color: textPri, fontSize: 12, fontWeight: 700 }}>{d.value}</span>
          <div style={{ width: "100%", background: surface2, borderRadius: 8, overflow: "hidden", height: 100, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${(d.value / max) * 100}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{ background: colorMap?.[d.label] || blue, borderRadius: 8 }}
            />
          </div>
          <span style={{ color: textSec, fontSize: 12, textTransform: "capitalize" }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Horizontal bar chart ──────────────────────────────────────────────────────
const HBarChart = ({ data }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {data.map((d) => (
        <div key={d.label}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: textSec, fontSize: 13, textTransform: "capitalize" }}>{d.label}</span>
            <span style={{ color: textPri, fontSize: 13, fontWeight: 700 }}>{d.value.toLocaleString()}</span>
          </div>
          <div style={{ background: surface2, borderRadius: 999, height: 10, overflow: "hidden" }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(d.value / max) * 100}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{ height: "100%", background: d.color || blue, borderRadius: 999 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};


// ════════════════════════════════════════════════════════════════════════════════
//  AdminPage
// ════════════════════════════════════════════════════════════════════════════════
const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn]         = useState(false);
  const [adminToken, setAdminToken]         = useState(localStorage.getItem("admin_token"));
  const [loginForm, setLoginForm]           = useState({ email: "", password: "" });
  const [analytics, setAnalytics]           = useState(null);
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  const [users, setUsers]                   = useState([]);
  const [topics, setTopics]                 = useState([]);
  const [companies, setCompanies]           = useState([]);
  const [subAdmins, setSubAdmins]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [generating, setGenerating]         = useState({});
  const [selectedUser, setSelectedUser]     = useState(null);

  const [newTopic, setNewTopic] = useState({
    name: "", description: "", slug: "", target_questions: 1000,
  });

  const [newUser, setNewUser]       = useState({ name: "", email: "", password: "" });
  const [editUser, setEditUser]     = useState({ id: "", name: "", email: "", is_active: true });
  const [newCompany, setNewCompany] = useState({ name: "", email: "", phone: "", address: "" });
  const [newSubAdmin, setNewSubAdmin] = useState({ name: "", email: "", password: "", company_id: "" });

  const [questions, setQuestions]                   = useState([]);
  const [selectedTopicId, setSelectedTopicId]       = useState("");
  const [questionsLoading, setQuestionsLoading]     = useState(false);
  const [questionsPagination, setQuestionsPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [newQuestion, setNewQuestion] = useState({
    question: "", options: ["", "", "", ""], correct_answer: 0,
    explanation: "", difficulty: "medium", subtopic: "",
    reference_url: "https://incometaxindia.gov.in/pages/acts/income-tax-act.aspx",
  });
  const [editQuestion, setEditQuestion] = useState(null);

  const [notification, setNotification] = useState({ message: "", topic_id: "" });
  const [dialogs, setDialogs] = useState({
    addUser: false, editUser: false, viewUser: false, addCompany: false,
    addSubAdmin: false, addQuestion: false, editQuestion: false, addTopic: false,
  });

  const [challengeAnalytics, setChallengeAnalytics] = useState(null);

  // UI-only state
  const [activeTab, setActiveTab]   = useState("dashboard");
  const [userSearch, setUserSearch] = useState("");

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (adminToken) verifyAdmin();
  }, []);

  const verifyAdmin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (response.ok) {
        setIsLoggedIn(true);
        // Don't set analytics here — fetchAllData will merge both endpoints properly
        fetchAllData(adminToken);
      } else {
        localStorage.removeItem("admin_token");
        setAdminToken(null);
      }
    } catch (error) {
      console.error("Admin verification failed:", error);
    }
  };

  const fetchAllData = async (token = adminToken) => {
    try {
      // Fetch both analytics endpoints so data shows regardless of backend version
      const [usersRes, topicsRes, basicRes, detailedRes, companiesRes, subAdminsRes] =
        await Promise.all([
          fetch(`${API_URL}/api/admin/users`,              { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/admin/topics`,             { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/admin/analytics`,          { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/admin/analytics/detailed`, { headers: { Authorization: `Bearer ${token}` } }),
          Promise.resolve({ ok: false }),  // companies removed
          fetch(`${API_URL}/api/admin/sub-admins`,         { headers: { Authorization: `Bearer ${token}` } }),
        ]);

      if (usersRes.ok)  setUsers((await usersRes.json()).users || []);
      if (topicsRes.ok) { const td = await topicsRes.json(); setTopics(Array.isArray(td) ? td : (td.topics || [])); }

      // Merge both analytics responses — new flat keys take priority, old nested keys as fallback
      const basic    = basicRes.ok    ? await basicRes.json()    : {};
      const detailed = detailedRes.ok ? await detailedRes.json() : {};

      const merged = {
        // KPI cards
        total_users:      basic.total_users      ?? detailed.users?.total     ?? 0,
        total_questions:  basic.total_questions  ?? basic.overview?.total_questions_in_bank
                                                 ?? detailed.question_bank?.total_questions ?? 0,
        total_quizzes:    basic.total_quizzes    ?? detailed.quizzes?.total   ?? 0,
        total_gp_awarded:         basic.total_gp_awarded         ?? 0,
        total_gp_repeat_removed:  basic.total_gp_repeat_removed  ?? 0,
        total_repeat_attempts:    basic.total_repeat_attempts     ?? 0,
        // Difficulty breakdown
        easy_questions:   basic.easy_questions   ?? basic.question_distribution?.easy       ?? 0,
        medium_questions: basic.medium_questions ?? basic.question_distribution?.medium     ?? 0,
        hard_questions:   basic.hard_questions   ?? basic.question_distribution?.hard       ?? 0,
        expert_questions: basic.expert_questions ?? basic.question_distribution?.expert     ?? 0,
        super_hard_questions: basic.super_hard_questions ?? basic.question_distribution?.super_hard ?? 0,
        gp_easy:          basic.gp_easy   ?? 0,
        gp_medium:        basic.gp_medium ?? 0,
        gp_hard:          basic.gp_hard   ?? 0,
        gp_expert:        basic.gp_expert ?? 0,
        gp_bonus:         basic.gp_bonus  ?? 0,
        // Engagement
        avg_accuracy:     basic.avg_accuracy    ?? detailed.quizzes?.average_score ?? 0,
        completion_rate:  basic.completion_rate ?? 0,
        // Lists
        daily_trend:     basic.daily_trend     ?? detailed.daily_trend     ?? [],
        top_performers:  basic.top_performers  ?? detailed.top_performers  ?? [],
        recent_activity: basic.recent_activity ?? detailed.recent_activity ?? [],
        // Passthrough
        challenges:          basic.challenges          ?? {},
        llm_key_configured:  basic.llm_key_configured  ?? false,
      };

      setAnalytics(merged);
      setDetailedAnalytics({
        users: {
          total:        merged.total_users,
          today_new:    detailed.users?.today_new    ?? basic.users_summary?.today_new    ?? 0,
          active_today: detailed.users?.active_today ?? basic.users_summary?.active_today ?? 0,
        },
        quizzes:        detailed.quizzes ?? {},
        daily_trend:    merged.daily_trend,
        top_performers: merged.top_performers,
        recent_activity: merged.recent_activity,
      });

      if (companiesRes.ok) { const cd = await companiesRes.json(); setCompanies(Array.isArray(cd) ? cd : (cd.companies || [])); }
      if (subAdminsRes.ok) { const sd = await subAdminsRes.json(); setSubAdmins(Array.isArray(sd) ? sd : (sd.sub_admins || [])); }

      // Challenge analytics (non-blocking — best effort)
      fetchChallengeAnalytics(token);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const fetchChallengeAnalytics = async (token = adminToken) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics/challenges`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setChallengeAnalytics(await res.json());
    } catch (e) {
      console.error("Challenge analytics fetch failed:", e);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await response.json();
      if (response.ok) {
        const newToken = data.access_token;
        localStorage.setItem("admin_token", newToken);
        setAdminToken(newToken);
        setIsLoggedIn(true);
        toast.success("Admin login successful!");
        fetchAllData(newToken);
      } else {
        toast.error(data.detail || "Login failed");
      }
    } catch (error) {
      toast.error("Connection error");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await fetchAllData();
    toast.success("Data refreshed!");
    setLoading(false);
  };

  // ── User management ────────────────────────────────────────────────────────
  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Please fill all fields"); return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(newUser),
      });
      if (response.ok) {
        toast.success("User created!");
        setNewUser({ name: "", email: "", password: "" });
        setDialogs({ ...dialogs, addUser: false });
        fetchAllData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create user");
      }
    } catch { toast.error("Connection error"); }
  };

  const updateUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${editUser.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name: editUser.name, email: editUser.email, is_active: editUser.is_active }),
      });
      if (response.ok) {
        toast.success("User updated!");
        setDialogs({ ...dialogs, editUser: false });
        fetchAllData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update user");
      }
    } catch { toast.error("Connection error"); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Permanently delete this user?")) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (response.ok) { toast.success("User deleted!"); fetchAllData(); }
    } catch { toast.error("Connection error"); }
  };

  const deleteTestUsers = async () => {
    if (!window.confirm("Delete ALL test users? This cannot be undone.")) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/users/bulk/test-users`, {
        method: "DELETE", headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Deleted ${data.deleted_count} test users`);
        fetchAllData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete test users");
      }
    } catch { toast.error("Connection error"); }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        // Backend returns { user, stats, quiz_history } — merge into flat object for the modal
        const userObj = data.user || data;
        const statsObj = data.stats || {};
        const merged = {
          ...userObj,
          // Prefer stats-computed values (more accurate) over user document values
          total_gp:   statsObj.total_gp   ?? userObj.total_gp   ?? 0,
          accuracy:   statsObj.accuracy   ?? userObj.accuracy   ?? 0,
          quizzes_taken: userObj.quizzes_taken ?? statsObj.total_sessions ?? 0,
          streak:     userObj.streak ?? 0,
          created_at: userObj.created_at,
          name:       userObj.name,
          email:      userObj.email,
        };
        setSelectedUser(merged);
        setDialogs({ ...dialogs, viewUser: true });
      }
    } catch { toast.error("Failed to fetch user details"); }
  };

  // ── Questions ──────────────────────────────────────────────────────────────
  const fetchQuestions = async (topicId, skip = 0) => {
    if (!topicId) return;
    setQuestionsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/topics/${topicId}/questions?skip=${skip}&limit=20`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions);
        setQuestionsPagination({ skip, limit: 20, total: data.total });
      }
    } catch { toast.error("Failed to fetch questions"); }
    finally { setQuestionsLoading(false); }
  };

  const handleTopicSelect = (topicId) => {
    setSelectedTopicId(topicId);
    setQuestionsPagination({ skip: 0, limit: 20, total: 0 });
    fetchQuestions(topicId, 0);
  };

  const createQuestion = async () => {
    if (!newQuestion.question || newQuestion.options.some(o => !o)) {
      toast.error("Please fill question and all options"); return;
    }
    if (!selectedTopicId) { toast.error("Please select a topic first"); return; }
    try {
      const response = await fetch(`${API_URL}/api/admin/questions`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...newQuestion, topic_id: selectedTopicId }),
      });
      if (response.ok) {
        toast.success("Question added!");
        setNewQuestion({
          question: "", options: ["", "", "", ""], correct_answer: 0,
          explanation: "", difficulty: "medium", subtopic: "",
          reference_url: "https://incometaxindia.gov.in/pages/acts/income-tax-act.aspx",
        });
        setDialogs({ ...dialogs, addQuestion: false });
        fetchQuestions(selectedTopicId, questionsPagination.skip);
        fetchAllData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to add question");
      }
    } catch { toast.error("Connection error"); }
  };

  const updateQuestion = async () => {
    if (!editQuestion) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/questions/${editQuestion.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(editQuestion),
      });
      if (response.ok) {
        toast.success("Question updated!");
        setDialogs({ ...dialogs, editQuestion: false });
        setEditQuestion(null);
        fetchQuestions(selectedTopicId, questionsPagination.skip);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update question");
      }
    } catch { toast.error("Connection error"); }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/questions/${questionId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (response.ok) {
        toast.success("Question deleted!");
        fetchQuestions(selectedTopicId, questionsPagination.skip);
        fetchAllData();
      }
    } catch { toast.error("Connection error"); }
  };

  // ── Companies & Sub-Admins ─────────────────────────────────────────────────
  const createCompany = async () => {
    if (!newCompany.name || !newCompany.email) {
      toast.error("Please fill name and email"); return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/companies`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(newCompany),
      });
      if (response.ok) {
        toast.success("Company registered!");
        setNewCompany({ name: "", email: "", phone: "", address: "" });
        setDialogs({ ...dialogs, addCompany: false });
        fetchAllData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to register company");
      }
    } catch { toast.error("Connection error"); }
  };

  const createSubAdmin = async () => {
    if (!newSubAdmin.name || !newSubAdmin.email || !newSubAdmin.password || !newSubAdmin.company_id) {
      toast.error("Please fill all fields and select a company"); return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/sub-admins`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(newSubAdmin),
      });
      if (response.ok) {
        toast.success("Sub-admin created!");
        setNewSubAdmin({ name: "", email: "", password: "", company_id: "" });
        setDialogs({ ...dialogs, addSubAdmin: false });
        fetchAllData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create sub-admin");
      }
    } catch { toast.error("Connection error"); }
  };

  // ── Topics ─────────────────────────────────────────────────────────────────
  const createTopic = async () => {
    if (!newTopic.name || !newTopic.slug) {
      toast.error("Please fill topic name and slug"); return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/topics`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...newTopic, is_active: true }),
      });
      if (response.ok) {
        toast.success("Topic created!");
        setNewTopic({ name: "", description: "", slug: "", target_questions: 1000 });
        setDialogs({ ...dialogs, addTopic: false });
        fetchAllData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create topic");
      }
    } catch { toast.error("Connection error"); }
  };

  const generateQuestions = async (topicId, batchSize = 25, numBatches = 1) => {
    setGenerating((prev) => ({ ...prev, [topicId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/admin/topics/${topicId}/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ topic_id: topicId, batch_size: batchSize, num_batches: numBatches }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Generated! Total: ${data.total_in_bank} questions`);
        fetchAllData();
        if (selectedTopicId === topicId) fetchQuestions(topicId, questionsPagination.skip);
      } else {
        toast.error(data.detail || "Generation failed - check LLM API key and credits");
      }
    } catch (error) {
      console.error("Generate error:", error);
      toast.error("Connection error - please try again");
    } finally {
      setGenerating((prev) => ({ ...prev, [topicId]: false }));
    }
  };

  const sendNotification = async () => {
    if (!notification.message) { toast.error("Please enter a message"); return; }
    try {
      const response = await fetch(`${API_URL}/api/admin/push-notification`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ message: notification.message, topic_id: notification.topic_id || null, user_ids: [] }),
      });
      if (response.ok) {
        toast.success("Notification sent!");
        setNotification({ message: "", topic_id: "" });
      }
    } catch { toast.error("Failed to send"); }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setAdminToken(null);
    setIsLoggedIn(false);
  };

  // ── Helper: user level ─────────────────────────────────────────────────────
  const getUserLevel = (gp) => {
    if (gp < 500)  return { label: "Bronze Tax Scholar",      color: "#ea580c" };
    if (gp < 1500) return { label: "Silver Tax Analyst",      color: "#64748b" };
    if (gp < 3500) return { label: "Gold Tax Advisor",        color: "#d97706" };
    if (gp < 7000) return { label: "Platinum Tax Strategist", color: "#4f46e5" };
    return            { label: "Diamond Tax Master",       color: "#0891b2" };
  };

  // ════════════════════════════════════════════════════════════════════════════
  //  LOGIN PAGE
  // ════════════════════════════════════════════════════════════════════════════
  if (!isLoggedIn) {
    return (
      <div style={{ minHeight: "100vh", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "Nunito, sans-serif" }}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} style={{ width: "100%", maxWidth: 440 }}>
          <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 24, padding: "40px 36px" }}>
            {/* Logo */}
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ width: 72, height: 72, borderRadius: 20, background: "linear-gradient(135deg, #10b981, #059669)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <Shield size={36} color="#fff" />
              </div>
              <h1 style={{ color: textPri, fontSize: 26, fontWeight: 800, margin: "0 0 6px" }}>🛡️ Admin Portal</h1>
              <p style={{ color: textSec, fontSize: 14, margin: 0 }}>Tax Genius — Restricted Access</p>
            </div>

            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email address</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  style={inputStyle}
                  placeholder="admin@taxgenius.com"
                  required
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  style={inputStyle}
                  placeholder="••••••••"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{ ...btnPrimary, width: "100%", justifyContent: "center", padding: "13px 20px", fontSize: 15, borderRadius: 12, opacity: loading ? 0.7 : 1 }}
              >
                {loading ? <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> : <Shield size={18} />}
                {loading ? "Authenticating…" : "Access Admin Panel"}
              </button>
            </form>

            <button onClick={() => navigate("/")} style={{ ...btnSecondary, width: "100%", justifyContent: "center", marginTop: 12, borderRadius: 12 }}>
              Back to Home
            </button>

            <p style={{ color: "#6b7280", fontSize: 12, textAlign: "center", marginTop: 24 }}>
              ⚠️ Authorised personnel only. All actions are logged and monitored.
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  DASHBOARD TABS CONTENT
  // ════════════════════════════════════════════════════════════════════════════

  // ── Tab: Dashboard ─────────────────────────────────────────────────────────
  const renderDashboard = () => {
    const diffStats = analytics
      ? [
          { label: "Basic",    value: analytics.easy_questions       || 0, color: green     },
          { label: "Moderate", value: analytics.medium_questions     || 0, color: amber     },
          { label: "Advanced", value: analytics.hard_questions       || 0, color: rose      },
          { label: "Expert",   value: analytics.expert_questions     || 0, color: "#7c3aed" },
          { label: "Super Hard", value: analytics.super_hard_questions || 0, color: "#a855f7" },
        ]
      : [];

    const gpDiff = analytics
      ? [
          { label: "Basic (Easy)",          value: analytics.gp_easy   || 0, color: green     },
          { label: "Moderate (Medium)",      value: analytics.gp_medium || 0, color: amber     },
          { label: "Advanced (Hard)",        value: analytics.gp_hard   || 0, color: rose      },
          { label: "Expert",                 value: analytics.gp_expert || 0, color: "#7c3aed" },
          { label: "Bonuses (Speed/Streak)", value: analytics.gp_bonus  || 0, color: "#6366f1" },
        ]
      : [];

    const recentUsers = [...users]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 5);

    return (
      <div>
        {/* KPI row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 28 }}>
          <KpiCard icon={Users}    label="Total Users"      value={analytics?.total_users?.toLocaleString()}     color={blue}   sub={detailedAnalytics?.users?.today_new ? `+${detailedAnalytics.users.today_new} today` : null} />
          <KpiCard icon={BookOpen} label="Total Questions"  value={analytics?.total_questions?.toLocaleString()} color={purple} />
          <KpiCard icon={Activity} label="Quizzes Taken"    value={analytics?.total_quizzes?.toLocaleString()}   color={green}  />
          <KpiCard icon={Award}    label="Total GP Awarded" value={analytics?.total_gp_awarded?.toLocaleString()} color={amber}  />
          <KpiCard icon={Activity} label="GP Removed (Repeats)" value={(analytics?.total_gp_repeat_removed || 0).toLocaleString()} color={rose} sub={analytics?.total_repeat_attempts ? `${analytics.total_repeat_attempts.toLocaleString()} repeat attempts` : null} />
        </div>

        {/* Charts row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
          <div style={cardStyle}>
            <h3 style={{ color: textPri, fontWeight: 800, marginBottom: 20, margin: "0 0 20px", fontSize: 15 }}>Quiz Activity by Difficulty</h3>
            {diffStats.length > 0
              ? <BarChart data={diffStats} colorMap={{ easy: green, medium: amber, hard: rose }} />
              : <p style={{ color: textSec, fontSize: 13 }}>No data available</p>
            }
          </div>
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <h3 style={{ color: textPri, fontWeight: 800, margin: 0, fontSize: 15 }}>GP Distribution by Difficulty</h3>
              <span style={{ fontSize: 11, color: amber, background: amber + "18", borderRadius: 999, padding: "3px 10px", fontWeight: 700, whiteSpace: "nowrap" }}>
                Total: {(analytics?.total_gp_awarded || 0).toLocaleString()} GP
              </span>
            </div>
            {gpDiff.length > 0
              ? <HBarChart data={gpDiff} />
              : <p style={{ color: textSec, fontSize: 13 }}>No data available</p>
            }
            <p style={{ color: textSec, fontSize: 11, marginTop: 12, marginBottom: 0 }}>
              GP is proportionally allocated per difficulty based on fresh correct answers. Bonuses = speed + streak GP not tied to a specific difficulty.
            </p>
          </div>
        </div>

        {/* Daily trend + Engagement metrics */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
          {/* 7-day quiz trend */}
          <div style={cardStyle}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 20px", fontSize: 15 }}>📈 Quiz Activity — Last 7 Days</h3>
            {(analytics?.daily_trend || []).length > 0 ? (() => {
              const trend = analytics.daily_trend;
              const maxVal = Math.max(...trend.map(d => d.quizzes), 1);
              return (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", height: 120 }}>
                  {trend.map((d, i) => (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <span style={{ color: textPri, fontSize: 11, fontWeight: 700 }}>{d.quizzes || 0}</span>
                      <div style={{ width: "100%", background: surface2, borderRadius: 6, overflow: "hidden", height: 80, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                        <div style={{ height: `${Math.max(4, (d.quizzes / maxVal) * 100)}%`, background: `linear-gradient(180deg, ${blue}, ${purple})`, borderRadius: 6, transition: "height 0.5s ease" }} />
                      </div>
                      <span style={{ color: textSec, fontSize: 11 }}>{d.day}</span>
                    </div>
                  ))}
                </div>
              );
            })() : <p style={{ color: textSec, fontSize: 13 }}>No quiz data yet</p>}
          </div>

          {/* Engagement mini stats */}
          <div style={cardStyle}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 18px", fontSize: 15 }}>📊 Engagement Metrics</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { label: "Avg Accuracy",       value: analytics?.avg_accuracy    ? `${analytics.avg_accuracy.toFixed(1)}%`    : "—", color: green  },
                { label: "Active Users Today",  value: detailedAnalytics?.users?.active_today != null ? String(detailedAnalytics.users.active_today) : "—", color: blue   },
                { label: "Completion Rate",     value: analytics?.completion_rate ? `${analytics.completion_rate.toFixed(1)}%` : "—", color: amber  },
                { label: "New Signups Today",   value: detailedAnalytics?.users?.today_new != null ? `+${detailedAnalytics.users.today_new}` : "—", color: purple },
              ].map((m) => (
                <div key={m.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: bg, borderRadius: 10, padding: "10px 16px" }}>
                  <span style={{ color: textSec, fontSize: 14 }}>{m.label}</span>
                  <span style={{ color: m.color, fontWeight: 800, fontSize: 20 }}>{m.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* Recent users */}
          <div style={cardStyle}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 16px", fontSize: 15 }}>Recent Signups</h3>
            {recentUsers.length === 0
              ? <p style={{ color: textSec, fontSize: 13 }}>No users yet</p>
              : recentUsers.map((u) => (
                <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${border}` }}>
                  <Avatar name={u.name} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: textPri, fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name}</div>
                    <div style={{ color: textSec, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ color: amber, fontSize: 13, fontWeight: 700 }}>{(u.total_gp || 0).toLocaleString()} GP</div>
                    <div style={{ color: textSec, fontSize: 11 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</div>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Topics overview */}
          <div style={cardStyle}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 16px", fontSize: 15 }}>Topics Overview</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {topics.slice(0, 6).map((t) => {
                const qCount = t.questions_count ?? t.question_count ?? 0;
                const qTarget = t.target_questions || 1000;
                const pct = qTarget > 0 ? Math.min(100, (qCount / qTarget) * 100) : 0;
                return (
                  <div key={t.id}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ color: textPri, fontSize: 13, fontWeight: 600 }}>{t.name}</span>
                      <span style={{ color: textSec, fontSize: 12 }}>{qCount.toLocaleString()} / {qTarget.toLocaleString()}</span>
                    </div>
                    <ProgressBar value={qCount} max={qTarget} color={pct >= 80 ? green : pct >= 40 ? amber : rose} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent quiz activity */}
        {(analytics?.recent_activity || []).length > 0 && (
          <div style={{ ...cardStyle, marginTop: 20 }}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 16px", fontSize: 15 }}>🕐 Recent Quiz Activity</h3>
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {["User", "Topic", "Score", "GP Earned", "Time"].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(analytics.recent_activity || []).slice(0, 8).map((a, i) => {
                    const pct = a.total_questions > 0 ? Math.round(a.correct_count / a.total_questions * 100) : 0;
                    const scoreColor = pct >= 70 ? green : pct >= 40 ? amber : rose;
                    return (
                      <tr key={i}>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Avatar name={a.user_name || "?"} size={28} />
                            <span style={{ fontWeight: 600 }}>{a.user_name || "—"}</span>
                          </div>
                        </td>
                        <td style={{ ...tdStyle, color: textSec, fontSize: 13 }}>{a.topic_name || "—"}</td>
                        <td style={tdStyle}>
                          <span style={{ color: scoreColor, fontWeight: 800 }}>{a.correct_count}/{a.total_questions}</span>
                          <span style={{ color: textSec, fontSize: 12, marginLeft: 6 }}>({pct}%)</span>
                        </td>
                        <td style={{ ...tdStyle, color: amber, fontWeight: 700 }}>+{a.gp_earned || 0} GP</td>
                        <td style={{ ...tdStyle, color: textSec, fontSize: 12 }}>
                          {a.completed_at ? new Date(a.completed_at.endsWith("Z") ? a.completed_at : a.completed_at + "Z").toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Tab: Users ─────────────────────────────────────────────────────────────
  const renderUsers = () => {
    const filtered = users.filter(u =>
      !userSearch ||
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase())
    );

    return (
      <div>
        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: textSec }} />
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users…"
              style={{ ...inputStyle, paddingLeft: 36 }}
            />
          </div>
          <button style={btnPrimary} onClick={() => setDialogs({ ...dialogs, addUser: true })}>
            <Plus size={16} /> Add User
          </button>
          <button style={btnDanger} onClick={deleteTestUsers}>
            <Trash2 size={16} /> Delete Test Users
          </button>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {["User", "Email", "GP", "Quizzes", "Accuracy", "Status", "Actions"].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: textSec, padding: 32 }}>No users found</td></tr>
                )}
                {filtered.map((u) => (
                  <tr key={u.id} style={{ background: u.is_active === false ? rose + "11" : "transparent" }}>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar name={u.name} size={32} />
                        <span style={{ fontWeight: 700 }}>{u.name}</span>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, color: textSec }}>{u.email}</td>
                    <td style={{ ...tdStyle, color: amber, fontWeight: 700 }}>{(u.total_gp || 0).toLocaleString()}</td>
                    <td style={{ ...tdStyle, color: textSec }}>{u.quizzes_taken || 0}</td>
                    <td style={{ ...tdStyle, color: green }}>{u.total_questions > 0 ? `${Math.round(u.correct_answers / u.total_questions * 100)}%` : "—"}</td>
                    <td style={tdStyle}>
                      <span style={{
                        background: u.is_active !== false ? green + "22" : rose + "22",
                        color: u.is_active !== false ? green : rose,
                        borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700,
                      }}>
                        {u.is_active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button style={{ ...btnIcon, color: blue }} title="View" onClick={() => viewUserDetails(u.id)}>
                          <Eye size={16} />
                        </button>
                        <button style={{ ...btnIcon, color: amber }} title="Edit" onClick={() => {
                          setEditUser({ id: u.id, name: u.name, email: u.email, is_active: u.is_active !== false });
                          setDialogs({ ...dialogs, editUser: true });
                        }}>
                          <Edit size={16} />
                        </button>
                        <button style={{ ...btnIcon, color: rose }} title="Delete" onClick={() => deleteUser(u.id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── View User Modal ── */}
        <Modal open={dialogs.viewUser} onClose={() => setDialogs({ ...dialogs, viewUser: false })} title="User Details" maxWidth={480}>
          {selectedUser && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, padding: 16, background: bg, borderRadius: 12 }}>
                <Avatar name={selectedUser.name} size={52} />
                <div>
                  <div style={{ color: textPri, fontWeight: 800, fontSize: 18 }}>{selectedUser.name}</div>
                  <div style={{ color: textSec, fontSize: 14 }}>{selectedUser.email}</div>
                  <div style={{ color: getUserLevel(selectedUser.total_gp || 0).color, fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                    {getUserLevel(selectedUser.total_gp || 0).label}
                  </div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  { label: "Total GP",    value: (selectedUser.total_gp || 0).toLocaleString(), color: amber },
                  { label: "Quizzes",     value: selectedUser.quizzes_taken || 0,               color: blue  },
                  { label: "Accuracy",    value: selectedUser.accuracy != null ? `${selectedUser.accuracy.toFixed(1)}%` : "—", color: green },
                  { label: "Streak",      value: `${selectedUser.streak || 0} days`,            color: purple },
                ].map(m => (
                  <div key={m.label} style={{ background: bg, borderRadius: 10, padding: "12px 16px" }}>
                    <div style={{ color: textSec, fontSize: 12 }}>{m.label}</div>
                    <div style={{ color: m.color, fontSize: 20, fontWeight: 800 }}>{m.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, padding: "12px 16px", background: bg, borderRadius: 10 }}>
                <div style={{ color: textSec, fontSize: 12, marginBottom: 4 }}>Joined</div>
                <div style={{ color: textPri, fontSize: 14 }}>
                  {selectedUser.created_at ? new Date(selectedUser.created_at.endsWith("Z") ? selectedUser.created_at : selectedUser.created_at + "Z").toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—"}
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* ── Edit User Modal ── */}
        <Modal open={dialogs.editUser} onClose={() => setDialogs({ ...dialogs, editUser: false })} title="Edit User">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input style={inputStyle} value={editUser.name} onChange={e => setEditUser({ ...editUser, name: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" style={inputStyle} value={editUser.email} onChange={e => setEditUser({ ...editUser, email: e.target.value })} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input type="checkbox" id="isActive" checked={editUser.is_active} onChange={e => setEditUser({ ...editUser, is_active: e.target.checked })} style={{ width: 16, height: 16, cursor: "pointer" }} />
              <label htmlFor="isActive" style={{ ...labelStyle, margin: 0, cursor: "pointer" }}>Active account</label>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={{ ...btnPrimary, flex: 1, justifyContent: "center" }} onClick={updateUser}>Save Changes</button>
              <button style={{ ...btnSecondary, flex: 1, justifyContent: "center" }} onClick={() => setDialogs({ ...dialogs, editUser: false })}>Cancel</button>
            </div>
          </div>
        </Modal>

        {/* ── Add User Modal ── */}
        <Modal open={dialogs.addUser} onClose={() => setDialogs({ ...dialogs, addUser: false })} title="Add New User">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input style={inputStyle} value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="John Doe" />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" style={inputStyle} value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="john@example.com" />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" style={inputStyle} value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="••••••••" />
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={{ ...btnPrimary, flex: 1, justifyContent: "center" }} onClick={createUser}><Plus size={16} /> Create User</button>
              <button style={{ ...btnSecondary, flex: 1, justifyContent: "center" }} onClick={() => setDialogs({ ...dialogs, addUser: false })}>Cancel</button>
            </div>
          </div>
        </Modal>
      </div>
    );
  };

  // ── Tab: Questions ─────────────────────────────────────────────────────────
  const QuestionForm = ({ q, setQ, onSubmit, submitLabel }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <label style={labelStyle}>Question</label>
        <textarea
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
          value={q.question}
          onChange={e => setQ({ ...q, question: e.target.value })}
          placeholder="Enter question text…"
        />
      </div>
      {[0, 1, 2, 3].map(i => (
        <div key={i}>
          <label style={labelStyle}>
            Option {i + 1}
            {q.correct_answer === i && <span style={{ color: green, marginLeft: 8, fontSize: 12 }}>✓ Correct</span>}
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={q.options[i]}
              onChange={e => {
                const opts = [...q.options]; opts[i] = e.target.value; setQ({ ...q, options: opts });
              }}
              placeholder={`Option ${i + 1}`}
            />
            <button
              style={{ ...btnIcon, background: q.correct_answer === i ? green + "33" : surface2, color: q.correct_answer === i ? green : textSec, border: `1px solid ${border}`, borderRadius: 8, padding: "8px 12px" }}
              onClick={() => setQ({ ...q, correct_answer: i })}
              title="Set as correct answer"
            >
              <CheckCircle size={16} />
            </button>
          </div>
        </div>
      ))}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={labelStyle}>Difficulty</label>
          <select style={selectStyle} value={q.difficulty} onChange={e => setQ({ ...q, difficulty: e.target.value })}>
            <option value="easy">Basic (Easy)</option>
            <option value="medium">Moderate (Medium)</option>
            <option value="hard">Advanced (Hard)</option>
            <option value="super_hard">Expert (Super Hard)</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Subtopic</label>
          <input style={inputStyle} value={q.subtopic} onChange={e => setQ({ ...q, subtopic: e.target.value })} placeholder="e.g. Section 80C" />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Explanation</label>
        <textarea rows={2} style={{ ...inputStyle, resize: "vertical" }} value={q.explanation} onChange={e => setQ({ ...q, explanation: e.target.value })} placeholder="Explain the correct answer…" />
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <button style={{ ...btnPrimary, flex: 1, justifyContent: "center" }} onClick={onSubmit}>{submitLabel}</button>
      </div>
    </div>
  );

  const renderQuestions = () => (
    <div>
      {/* AI disclaimer banner */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, background: amber + "18", border: `1px solid ${amber}44`, borderRadius: 12, padding: "10px 16px", marginBottom: 18 }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
        <p style={{ color: amber, fontSize: 13, fontWeight: 600, margin: 0, fontFamily: "Nunito, sans-serif" }}>
          Questions generated by AI — please verify section references before use in professional context.
        </p>
      </div>

      {/* Topic selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <select
            style={selectStyle}
            value={selectedTopicId}
            onChange={e => handleTopicSelect(e.target.value)}
          >
            <option value="">— Select a topic —</option>
            {topics.map(t => (
              <option key={t.id} value={t.id}>{t.name} ({t.questions_count ?? t.question_count ?? 0} questions)</option>
            ))}
          </select>
        </div>
        <button
          style={{ ...btnPrimary, opacity: !selectedTopicId ? 0.5 : 1 }}
          disabled={!selectedTopicId}
          onClick={() => setDialogs({ ...dialogs, addQuestion: true })}
        >
          <Plus size={16} /> Add Question
        </button>
      </div>

      {/* Questions cards */}
      {questionsLoading
        ? <div style={{ ...cardStyle, textAlign: "center", padding: 48, color: textSec }}>
            <Loader2 size={28} style={{ animation: "spin 1s linear infinite", marginBottom: 8 }} />
            <div>Loading questions…</div>
          </div>
        : !selectedTopicId
        ? <div style={{ ...cardStyle, textAlign: "center", padding: 48, color: textSec }}>Select a topic to view questions</div>
        : questions.length === 0
        ? <div style={{ ...cardStyle, textAlign: "center", padding: 48, color: textSec }}>No questions in this topic</div>
        : (
          <>
            {/* Pagination header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ color: textSec, fontSize: 13, fontWeight: 600 }}>
                Showing {questionsPagination.skip + 1}–{Math.min(questionsPagination.skip + questionsPagination.limit, questionsPagination.total)} of {questionsPagination.total} questions
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{ ...btnSecondary, opacity: questionsPagination.skip === 0 ? 0.4 : 1 }}
                  disabled={questionsPagination.skip === 0}
                  onClick={() => fetchQuestions(selectedTopicId, Math.max(0, questionsPagination.skip - questionsPagination.limit))}
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                <button
                  style={{ ...btnSecondary, opacity: questionsPagination.skip + questionsPagination.limit >= questionsPagination.total ? 0.4 : 1 }}
                  disabled={questionsPagination.skip + questionsPagination.limit >= questionsPagination.total}
                  onClick={() => fetchQuestions(selectedTopicId, questionsPagination.skip + questionsPagination.limit)}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>

            {/* Question Cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {questions.map((q, idx) => {
                const optionLetters = ["A", "B", "C", "D", "E"];
                return (
                  <div key={q.id} style={{ ...cardStyle, padding: "20px 24px" }}>
                    {/* Header row */}
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
                        {/* Number */}
                        <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: green + "22", color: green, fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {questionsPagination.skip + idx + 1}
                        </span>
                        {/* Question text — full, no truncation */}
                        <p style={{ color: textPri, fontWeight: 700, fontSize: 15, lineHeight: 1.5, margin: 0, flex: 1 }}>
                          {q.question}
                        </p>
                      </div>
                      {/* Badges + Actions */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                        <DiffBadge diff={q.difficulty} />
                        {q.subtopic && (
                          <span style={{ fontSize: 11, fontWeight: 700, background: blue + "22", color: blue, borderRadius: 999, padding: "3px 10px" }}>
                            {q.subtopic}
                          </span>
                        )}
                        <button style={{ ...btnIcon, color: amber }} onClick={() => { setEditQuestion({ ...q }); setDialogs({ ...dialogs, editQuestion: true }); }}>
                          <Edit size={15} />
                        </button>
                        <button style={{ ...btnIcon, color: rose }} onClick={() => deleteQuestion(q.id)}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                    {/* Options */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8, marginBottom: q.explanation ? 12 : 0 }}>
                      {(q.options || []).map((opt, oi) => {
                        const isCorrect = opt === q.correct_answer;
                        return (
                          <div key={oi} style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "9px 14px", borderRadius: 10,
                            background: isCorrect ? green + "18" : "#1e293b",
                            border: `1.5px solid ${isCorrect ? green : border}`,
                          }}>
                            <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", background: isCorrect ? green : "#334155", color: isCorrect ? "#fff" : textSec, fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {optionLetters[oi]}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: isCorrect ? 700 : 500, color: isCorrect ? green : textPri }}>
                              {opt}
                            </span>
                            {isCorrect && <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800, color: green }}>✓ Correct</span>}
                          </div>
                        );
                      })}
                    </div>

                    {/* Explanation */}
                    {q.explanation && (
                      <div style={{ background: "#1a2a1a", border: `1px solid ${green}33`, borderRadius: 10, padding: "10px 14px", marginTop: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: green, textTransform: "uppercase", letterSpacing: 0.5 }}>Explanation · </span>
                        <span style={{ fontSize: 13, color: textSec, lineHeight: 1.5 }}>{q.explanation}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination footer */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
              <span style={{ color: textSec, fontSize: 13, fontWeight: 600 }}>
                {questionsPagination.skip + 1}–{Math.min(questionsPagination.skip + questionsPagination.limit, questionsPagination.total)} of {questionsPagination.total}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{ ...btnSecondary, opacity: questionsPagination.skip === 0 ? 0.4 : 1 }}
                  disabled={questionsPagination.skip === 0}
                  onClick={() => fetchQuestions(selectedTopicId, Math.max(0, questionsPagination.skip - questionsPagination.limit))}
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <button
                  style={{ ...btnSecondary, opacity: questionsPagination.skip + questionsPagination.limit >= questionsPagination.total ? 0.4 : 1 }}
                  disabled={questionsPagination.skip + questionsPagination.limit >= questionsPagination.total}
                  onClick={() => fetchQuestions(selectedTopicId, questionsPagination.skip + questionsPagination.limit)}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )
      }

      {/* Add Question Modal */}
      <Modal open={dialogs.addQuestion} onClose={() => setDialogs({ ...dialogs, addQuestion: false })} title="Add Question" maxWidth={600}>
        <QuestionForm q={newQuestion} setQ={setNewQuestion} onSubmit={createQuestion} submitLabel="Add Question" />
      </Modal>

      {/* Edit Question Modal */}
      <Modal open={dialogs.editQuestion} onClose={() => { setDialogs({ ...dialogs, editQuestion: false }); setEditQuestion(null); }} title="Edit Question" maxWidth={600}>
        {editQuestion && <QuestionForm q={editQuestion} setQ={setEditQuestion} onSubmit={updateQuestion} submitLabel="Save Changes" />}
      </Modal>
    </div>
  );

  // ── Tab: Topics ────────────────────────────────────────────────────────────
  const renderTopics = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ color: textPri, fontWeight: 800, margin: 0, fontSize: 18 }}>Topics ({topics.length})</h2>
        <button style={btnPrimary} onClick={() => setDialogs({ ...dialogs, addTopic: true })}>
          <Plus size={16} /> Add Topic
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {topics.map((t) => {
          const qCount = t.questions_count ?? t.question_count ?? 0;
          const pct = t.target_questions > 0 ? Math.min(100, (qCount / t.target_questions) * 100) : 0;
          const barColor = pct >= 80 ? green : pct >= 40 ? amber : rose;
          return (
            <div key={t.id} style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 4px", fontSize: 16 }}>{t.name}</h3>
                  <span style={{ background: surface2, color: textSec, fontSize: 11, padding: "2px 8px", borderRadius: 999, marginLeft: 8, flexShrink: 0 }}>{t.slug}</span>
                </div>
                {t.description && <p style={{ color: textSec, fontSize: 13, margin: 0 }}>{t.description}</p>}
              </div>

              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: textSec, fontSize: 13 }}>Questions</span>
                  <span style={{ color: barColor, fontSize: 13, fontWeight: 700 }}>{qCount} / {t.target_questions || 1000}</span>
                </div>
                <ProgressBar value={qCount} max={t.target_questions || 1000} color={barColor} />
                <div style={{ color: textSec, fontSize: 11, marginTop: 4 }}>{pct.toFixed(1)}% to target</div>
              </div>

              <button
                style={{ ...btnPrimary, width: "100%", justifyContent: "center", opacity: generating[t.id] ? 0.7 : 1 }}
                disabled={generating[t.id]}
                onClick={() => generateQuestions(t.id)}
              >
                {generating[t.id]
                  ? <><Loader2 size={15} style={{ animation: "spin 1s linear infinite" }} /> Generating…</>
                  : <><Zap size={15} /> Generate Questions</>
                }
              </button>
            </div>
          );
        })}
      </div>

      {/* Add Topic Modal */}
      <Modal open={dialogs.addTopic} onClose={() => setDialogs({ ...dialogs, addTopic: false })} title="Add New Topic">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle}>Topic Name</label>
            <input style={inputStyle} value={newTopic.name} onChange={e => setNewTopic({ ...newTopic, name: e.target.value })} placeholder="e.g. Income Tax Basics" />
          </div>
          <div>
            <label style={labelStyle}>Slug</label>
            <input style={inputStyle} value={newTopic.slug} onChange={e => setNewTopic({ ...newTopic, slug: e.target.value })} placeholder="e.g. income-tax-basics" />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={newTopic.description} onChange={e => setNewTopic({ ...newTopic, description: e.target.value })} placeholder="Brief description…" />
          </div>
          <div>
            <label style={labelStyle}>Target Questions</label>
            <input type="number" style={inputStyle} value={newTopic.target_questions} onChange={e => setNewTopic({ ...newTopic, target_questions: parseInt(e.target.value) || 1000 })} />
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button style={{ ...btnPrimary, flex: 1, justifyContent: "center" }} onClick={createTopic}><Plus size={16} /> Create Topic</button>
            <button style={{ ...btnSecondary, flex: 1, justifyContent: "center" }} onClick={() => setDialogs({ ...dialogs, addTopic: false })}>Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );

  // ── Tab: Analytics ─────────────────────────────────────────────────────────
  const renderAnalytics = () => {
    // Compute user distribution
    const levels = [
      { label: "🥉 Bronze Tax Scholar",      min: 0,    max: 500,      color: "#ea580c" },
      { label: "🥈 Silver Tax Analyst",      min: 500,  max: 1500,     color: "#64748b" },
      { label: "🥇 Gold Tax Advisor",        min: 1500, max: 3500,     color: "#d97706" },
      { label: "💎 Platinum Tax Strategist", min: 3500, max: 7000,     color: "#4f46e5" },
      { label: "👑 Diamond Tax Master",      min: 7000, max: Infinity, color: "#0891b2" },
    ];

    const levelCounts = levels.map(l => ({
      label: l.label,
      value: users.filter(u => (u.total_gp || 0) >= l.min && (u.total_gp || 0) < l.max).length,
      color: l.color,
    }));

    const top10 = [...users]
      .sort((a, b) => (b.total_gp || 0) - (a.total_gp || 0))
      .slice(0, 10);

    const totalGP    = users.reduce((s, u) => s + (u.total_gp || 0), 0);
    const avgGP      = users.length ? Math.round(totalGP / users.length) : 0;
    const highestGP  = users.length ? Math.max(...users.map(u => u.total_gp || 0)) : 0;

    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
          {/* Level distribution */}
          <div style={cardStyle}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 20px", fontSize: 15 }}>User Distribution by Level</h3>
            <HBarChart data={levelCounts} />
          </div>

          {/* GP Economy */}
          <div style={cardStyle}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 20px", fontSize: 15 }}>GP Economy Stats</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { label: "Total GP Distributed",   value: totalGP.toLocaleString(),              color: amber  },
                { label: "Average GP per User",    value: avgGP.toLocaleString(),                color: green  },
                { label: "Highest Single User",    value: highestGP.toLocaleString(),            color: purple },
                { label: "Total Questions Pool",   value: (analytics?.total_questions || 0).toLocaleString(), color: blue  },
                { label: "🚫 GP Removed (Repeats)", value: (analytics?.total_gp_repeat_removed || 0).toLocaleString(), color: rose },
                { label: "Repeat Attempts Blocked", value: (analytics?.total_repeat_attempts || 0).toLocaleString(), color: rose },
              ].map(s => (
                <div key={s.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: bg, borderRadius: 10 }}>
                  <span style={{ color: textSec, fontSize: 14 }}>{s.label}</span>
                  <span style={{ color: s.color, fontWeight: 800, fontSize: 18 }}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Difficulty performance */}
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 20px", fontSize: 15 }}>Difficulty Performance</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {[
              { diff: "Easy",   questions: analytics?.easy_questions   || 0, gp: analytics?.gp_easy   || 0, color: green    },
              { diff: "Medium", questions: analytics?.medium_questions || 0, gp: analytics?.gp_medium || 0, color: amber    },
              { diff: "Hard",   questions: analytics?.hard_questions   || 0, gp: analytics?.gp_hard   || 0, color: rose     },
              { diff: "Expert", questions: analytics?.expert_questions || 0, gp: analytics?.gp_expert || 0, color: "#7c3aed" },
            ].map(d => (
              <div key={d.diff} style={{ background: bg, borderRadius: 14, padding: 20, textAlign: "center", border: `1px solid ${d.color}33` }}>
                <DiffBadge diff={d.diff.toLowerCase()} />
                <div style={{ color: d.color, fontSize: 32, fontWeight: 800, margin: "14px 0 4px" }}>{d.questions.toLocaleString()}</div>
                <div style={{ color: textSec, fontSize: 13 }}>Questions</div>
                <div style={{ color: textPri, fontSize: 16, fontWeight: 700, marginTop: 10 }}>{d.gp.toLocaleString()} GP</div>
                <div style={{ color: textSec, fontSize: 12 }}>GP pool</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top 10 Users */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "20px 20px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: 0, fontSize: 15 }}>Top 10 Users by GP</h3>
            <span style={{ fontSize: 11, color: rose, background: rose + "18", borderRadius: 999, padding: "3px 10px", fontWeight: 700 }}>
              🚫 Repeat-question GP removed — scores reflect unique questions only
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {["Rank", "User", "Email", "GP", "Quizzes", "Accuracy", "Level"].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top10.map((u, i) => {
                  const lv = getUserLevel(u.total_gp || 0);
                  return (
                    <tr key={u.id}>
                      <td style={tdStyle}>
                        <span style={{
                          background: i < 3 ? [amber, textSec, "#cd7f32"][i] + "22" : surface2,
                          color:       i < 3 ? [amber, textSec, "#cd7f32"][i] : textSec,
                          borderRadius: 999, padding: "2px 10px", fontWeight: 800, fontSize: 14,
                        }}>#{i + 1}</span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar name={u.name} size={32} />
                          <span style={{ fontWeight: 700 }}>{u.name}</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: textSec }}>{u.email}</td>
                      <td style={{ ...tdStyle, color: amber, fontWeight: 800 }}>{(u.total_gp || 0).toLocaleString()}</td>
                      <td style={{ ...tdStyle, color: textSec }}>{u.quizzes_taken || 0}</td>
                      <td style={{ ...tdStyle, color: green }}>{u.total_questions > 0 ? `${Math.round(u.correct_answers / u.total_questions * 100)}%` : "—"}</td>
                      <td style={tdStyle}><span style={{ color: lv.color, fontWeight: 700, fontSize: 13 }}>{lv.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── Tab: Companies ─────────────────────────────────────────────────────────
  const renderCompanies = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ color: textPri, fontWeight: 800, margin: 0, fontSize: 18 }}>Companies ({companies.length})</h2>
        <button style={btnPrimary} onClick={() => setDialogs({ ...dialogs, addCompany: true })}>
          <Plus size={16} /> Add Company
        </button>
      </div>

      {companies.length === 0
        ? <div style={{ ...cardStyle, textAlign: "center", padding: 48, color: textSec }}>No companies registered yet</div>
        : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            {companies.map((c) => (
              <div key={c.id} style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: blue + "22", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Building size={20} color={blue} />
                  </div>
                  <div>
                    <div style={{ color: textPri, fontWeight: 800, fontSize: 15 }}>{c.name}</div>
                    <div style={{ color: textSec, fontSize: 12 }}>{c.email}</div>
                  </div>
                </div>
                {c.phone   && <div style={{ color: textSec, fontSize: 13 }}>📞 {c.phone}</div>}
                {c.address && <div style={{ color: textSec, fontSize: 13 }}>📍 {c.address}</div>}
                {c.created_at && (
                  <div style={{ color: textSec, fontSize: 12, marginTop: 4, borderTop: `1px solid ${border}`, paddingTop: 10 }}>
                    Added {new Date(c.created_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      }

      {/* Add Company Modal */}
      <Modal open={dialogs.addCompany} onClose={() => setDialogs({ ...dialogs, addCompany: false })} title="Register Company">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle}>Company Name *</label>
            <input style={inputStyle} value={newCompany.name} onChange={e => setNewCompany({ ...newCompany, name: e.target.value })} placeholder="Acme Corp" />
          </div>
          <div>
            <label style={labelStyle}>Email *</label>
            <input type="email" style={inputStyle} value={newCompany.email} onChange={e => setNewCompany({ ...newCompany, email: e.target.value })} placeholder="contact@acmecorp.com" />
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input style={inputStyle} value={newCompany.phone} onChange={e => setNewCompany({ ...newCompany, phone: e.target.value })} placeholder="+91 98765 43210" />
          </div>
          <div>
            <label style={labelStyle}>Address</label>
            <textarea rows={2} style={{ ...inputStyle, resize: "vertical" }} value={newCompany.address} onChange={e => setNewCompany({ ...newCompany, address: e.target.value })} placeholder="123 Business Park, Mumbai" />
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button style={{ ...btnPrimary, flex: 1, justifyContent: "center" }} onClick={createCompany}><Building size={16} /> Register</button>
            <button style={{ ...btnSecondary, flex: 1, justifyContent: "center" }} onClick={() => setDialogs({ ...dialogs, addCompany: false })}>Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Sub-admins section */}
      <div style={{ marginTop: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ color: textPri, fontWeight: 800, margin: 0, fontSize: 18 }}>Sub-Admins ({subAdmins.length})</h2>
          <button style={btnPrimary} onClick={() => setDialogs({ ...dialogs, addSubAdmin: true })}>
            <UserPlus size={16} /> Add Sub-Admin
          </button>
        </div>

        {subAdmins.length === 0
          ? <div style={{ ...cardStyle, textAlign: "center", padding: 48, color: textSec }}>No sub-admins yet</div>
          : (
            <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {["Name", "Email", "Company", "Created"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {subAdmins.map(s => (
                    <tr key={s.id}>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar name={s.name} size={32} />
                          <span style={{ fontWeight: 700 }}>{s.name}</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: textSec }}>{s.email}</td>
                      <td style={{ ...tdStyle, color: blue }}>
                        {companies.find(c => c.id === s.company_id)?.name || s.company_id || "—"}
                      </td>
                      <td style={{ ...tdStyle, color: textSec }}>
                        {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }

        {/* Add Sub-Admin Modal */}
        <Modal open={dialogs.addSubAdmin} onClose={() => setDialogs({ ...dialogs, addSubAdmin: false })} title="Add Sub-Admin">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input style={inputStyle} value={newSubAdmin.name} onChange={e => setNewSubAdmin({ ...newSubAdmin, name: e.target.value })} placeholder="Jane Smith" />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" style={inputStyle} value={newSubAdmin.email} onChange={e => setNewSubAdmin({ ...newSubAdmin, email: e.target.value })} placeholder="jane@company.com" />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" style={inputStyle} value={newSubAdmin.password} onChange={e => setNewSubAdmin({ ...newSubAdmin, password: e.target.value })} placeholder="••••••••" />
            </div>
            <div>
              <label style={labelStyle}>Company</label>
              <select style={selectStyle} value={newSubAdmin.company_id} onChange={e => setNewSubAdmin({ ...newSubAdmin, company_id: e.target.value })}>
                <option value="">— Select company —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={{ ...btnPrimary, flex: 1, justifyContent: "center" }} onClick={createSubAdmin}><UserPlus size={16} /> Create</button>
              <button style={{ ...btnSecondary, flex: 1, justifyContent: "center" }} onClick={() => setDialogs({ ...dialogs, addSubAdmin: false })}>Cancel</button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );

  // ── Tab: Challenges ────────────────────────────────────────────────────────
  const renderChallenges = () => {
    const ca = challengeAnalytics;
    if (!ca) return (
      <div style={{ textAlign: "center", padding: 80, color: textSec }}>
        <Swords size={40} style={{ marginBottom: 16, opacity: 0.4 }} />
        <p>Loading challenge analytics…</p>
      </div>
    );

    const stats = ca.stats || {};
    const trendData = (ca.daily_trend || []).map(d => ({ label: d.day, value: d.challenges }));
    const leaderboard = ca.leaderboard || [];
    const allCh = ca.all_challenges || [];
    const suspicious = ca.suspicious || [];

    const statusColor = { pending: amber, active: blue, completed: green, declined: rose };

    return (
      <div>
        {/* ── Section 1: KPI row ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 28 }}>
          <KpiCard icon={Target}   label="Total Challenges"  value={stats.total ?? "—"}            color={blue}   />
          <KpiCard icon={CheckCircle} label="Completed"      value={stats.completed ?? "—"}         color={green}  />
          <KpiCard icon={Activity} label="Active"            value={stats.active ?? "—"}             color={amber}  />
          <KpiCard icon={TrendingUp} label="Completion Rate" value={stats.completion_rate != null ? `${stats.completion_rate}%` : "—"} color={purple} />
          <KpiCard icon={Zap}      label="Ties"              value={stats.tie_count ?? "—"}          color={rose}   />
        </div>

        {/* ── Section 2: Trend + Leaderboard ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
          {/* Daily trend */}
          <div style={cardStyle}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: "0 0 20px", fontSize: 15 }}>⚔️ Challenges — Last 7 Days</h3>
            {trendData.length > 0 ? (() => {
              const maxVal = Math.max(...trendData.map(d => d.value), 1);
              return (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", height: 120 }}>
                  {trendData.map((d, i) => (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <span style={{ color: textPri, fontSize: 11, fontWeight: 700 }}>{d.value}</span>
                      <div style={{ width: "100%", background: surface2, borderRadius: 6, overflow: "hidden", height: 80, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                        <div style={{ height: `${Math.max(4, (d.value / maxVal) * 100)}%`, background: `linear-gradient(180deg, ${blue}, ${purple})`, borderRadius: 6, transition: "height 0.5s ease" }} />
                      </div>
                      <span style={{ color: textSec, fontSize: 11 }}>{d.label}</span>
                    </div>
                  ))}
                </div>
              );
            })() : <p style={{ color: textSec, fontSize: 13 }}>No challenge data yet</p>}
          </div>

          {/* Leaderboard top 10 */}
          <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "16px 20px 12px" }}>
              <h3 style={{ color: textPri, fontWeight: 800, margin: 0, fontSize: 15 }}>🏆 Top Challengers</h3>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {["Rank", "Name", "Wins", "Losses", "Win Rate", "Avg Score"].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.slice(0, 10).map((u, i) => (
                    <tr key={u.user_id}>
                      <td style={tdStyle}>
                        <span style={{
                          background: i < 3 ? [amber, textSec, "#cd7f32"][i] + "22" : surface2,
                          color:       i < 3 ? [amber, textSec, "#cd7f32"][i] : textSec,
                          borderRadius: 999, padding: "2px 10px", fontWeight: 800, fontSize: 13,
                        }}>#{i + 1}</span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Avatar name={u.name} size={28} />
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{u.name}</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: green, fontWeight: 800 }}>{u.wins}</td>
                      <td style={{ ...tdStyle, color: rose }}>{u.losses}</td>
                      <td style={{ ...tdStyle, color: blue, fontWeight: 700 }}>{u.win_rate}%</td>
                      <td style={{ ...tdStyle, color: amber, fontWeight: 700 }}>{u.avg_score}</td>
                    </tr>
                  ))}
                  {leaderboard.length === 0 && (
                    <tr><td colSpan={6} style={{ ...tdStyle, textAlign: "center", color: textSec }}>No completed challenges yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Section 3: All Challenges table ─── */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden", marginBottom: 28 }}>
          <div style={{ padding: "16px 20px 12px" }}>
            <h3 style={{ color: textPri, fontWeight: 800, margin: 0, fontSize: 15 }}>All Challenges (last 100)</h3>
          </div>
          <div style={{ overflowX: "auto", maxHeight: 480, overflowY: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {["Challenger", "vs.", "Challenged", "Difficulty", "Status", "Scores", "Winner", "Date"].map(h => (
                    <th key={h} style={{ ...thStyle, position: "sticky", top: 0, zIndex: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allCh.map((c, i) => {
                  const stColor = statusColor[c.status] || textSec;
                  const winnerLabel = c.winner_id === "tie"
                    ? <span style={{ color: amber, fontWeight: 700 }}>Tie</span>
                    : c.winner_id === c.challenger_name || c.winner_id
                      ? <span style={{ color: green, fontWeight: 700, fontSize: 12 }}>
                          {c.winner_id === c.challenger_name ? c.challenger_name : (
                            // winner_id is a user ID — show name from leaderboard or fallback
                            leaderboard.find(l => l.user_id === c.winner_id)?.name || c.challenger_name || c.winner_id
                          )}
                        </span>
                      : <span style={{ color: textSec }}>—</span>;
                  return (
                    <tr key={c.id || i}>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Avatar name={c.challenger_name} size={24} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{c.challenger_name || "—"}</div>
                            <div style={{ color: textSec, fontSize: 11 }}>{c.challenger_email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: textSec, textAlign: "center", fontWeight: 800 }}>⚔️</td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Avatar name={c.challenged_name} size={24} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{c.challenged_name || "—"}</div>
                            <div style={{ color: textSec, fontSize: 11 }}>{c.challenged_email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ background: surface2, color: textSec, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                          {c.difficulty || "—"}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ background: stColor + "22", color: stColor, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
                          {c.status}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12 }}>
                        <span style={{ color: blue }}>{c.challenger_score ?? "—"}</span>
                        <span style={{ color: textSec }}> / </span>
                        <span style={{ color: amber }}>{c.challenged_score ?? "—"}</span>
                      </td>
                      <td style={tdStyle}>{winnerLabel}</td>
                      <td style={{ ...tdStyle, color: textSec, fontSize: 12, whiteSpace: "nowrap" }}>
                        {c.created_at ? new Date(c.created_at.endsWith("Z") ? c.created_at : c.created_at + "Z").toLocaleDateString("en-IN") : "—"}
                      </td>
                    </tr>
                  );
                })}
                {allCh.length === 0 && (
                  <tr><td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: textSec }}>No challenges found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Section 4: Suspicious Activity ─── */}
        {suspicious.length > 0 && (
          <div style={{ ...cardStyle, padding: 0, overflow: "hidden", border: `1px solid ${rose}44` }}>
            <div style={{ padding: "16px 20px 12px", background: rose + "11", borderBottom: `1px solid ${rose}33` }}>
              <h3 style={{ color: rose, fontWeight: 800, margin: 0, fontSize: 15 }}>🚨 Suspicious Activity Detected ({suspicious.length})</h3>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {["Severity", "Flag Type", "User", "Opponent", "Description", "Details"].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {suspicious.map((s, i) => {
                    const sevColor = s.severity === "HIGH" ? rose : amber;
                    const detailLabel = (() => {
                      const d = s.details || {};
                      if (d.total_matches != null) return `${d.wins}/${d.total_matches} wins`;
                      if (d.time_seconds  != null) return `${d.time_seconds.toFixed(1)}s`;
                      if (d.quiz_sessions != null) return `${d.quiz_sessions} quizzes`;
                      if (d.perfect_score_count != null) return `${d.perfect_score_count}× 15/15`;
                      return JSON.stringify(d);
                    })();
                    return (
                      <tr key={i}>
                        <td style={tdStyle}>
                          <span style={{ background: sevColor + "22", color: sevColor, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 800 }}>
                            {s.severity}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, color: textSec, fontSize: 12, whiteSpace: "nowrap" }}>{s.flag_type}</td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Avatar name={s.user_name} size={24} />
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13 }}>{s.user_name}</div>
                              <div style={{ color: textSec, fontSize: 11 }}>{s.user_email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {s.opponent_name
                            ? <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <Avatar name={s.opponent_name} size={24} />
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: 13 }}>{s.opponent_name}</div>
                                  <div style={{ color: textSec, fontSize: 11 }}>{s.opponent_email}</div>
                                </div>
                              </div>
                            : <span style={{ color: textSec }}>—</span>
                          }
                        </td>
                        <td style={{ ...tdStyle, color: textSec, fontSize: 13, maxWidth: 300 }}>{s.description}</td>
                        <td style={tdStyle}>
                          <span style={{ background: sevColor + "22", color: sevColor, borderRadius: 6, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>
                            {detailLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  //  MAIN LAYOUT
  // ════════════════════════════════════════════════════════════════════════════

  const tabs = [
    { id: "dashboard",  label: "Dashboard",   icon: BarChart2  },
    { id: "users",      label: "Users",       icon: Users      },
    { id: "questions",  label: "Questions",   icon: BookOpen   },
    { id: "topics",     label: "Topics",      icon: Database   },
    { id: "analytics",  label: "Analytics",   icon: TrendingUp },
    { id: "challenges", label: "Challenges ⚔️", icon: Swords   },
  ];

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "Nunito, sans-serif" }}>
      {/* ── Spinner keyframe injection ── */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* ── Top Navbar ── */}
      <header style={{ background: surface, borderBottom: `1px solid ${border}`, position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 24px", height: 64, display: "flex", alignItems: "center", gap: 24 }}>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg, #10b981, #059669)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Shield size={20} color="#fff" />
            </div>
            <span style={{ color: textPri, fontWeight: 900, fontSize: 17 }}>🛡️ Tax Genius Admin</span>
          </div>

          {/* Tab nav */}
          <nav style={{ display: "flex", alignItems: "center", gap: 4, flex: 1, justifyContent: "center", flexWrap: "nowrap", overflowX: "auto" }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background:   activeTab === tab.id ? green + "22" : "transparent",
                  color:        activeTab === tab.id ? green : textSec,
                  border:       activeTab === tab.id ? `1px solid ${green}44` : "1px solid transparent",
                  borderRadius: 999,
                  padding:      "7px 16px",
                  fontSize:     13,
                  fontWeight:   activeTab === tab.id ? 800 : 600,
                  cursor:       "pointer",
                  display:      "flex",
                  alignItems:   "center",
                  gap:          6,
                  whiteSpace:   "nowrap",
                  transition:   "all 0.15s ease",
                  fontFamily:   "Nunito, sans-serif",
                }}
              >
                <tab.icon size={15} />
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Right actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <button
              style={{ ...btnSecondary, padding: "8px 14px" }}
              onClick={refreshData}
              title="Refresh data"
            >
              <RefreshCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
              <span style={{ fontSize: 13 }}>Refresh</span>
            </button>
            <button style={{ ...btnDanger, padding: "8px 14px" }} onClick={handleLogout}>
              <LogOut size={16} />
              <span style={{ fontSize: 13 }}>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Page content ── */}
      <main style={{ maxWidth: 1400, margin: "0 auto", padding: "32px 24px" }}>
        <motion.div key={activeTab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
          {activeTab === "dashboard"  && renderDashboard()}
          {activeTab === "users"      && renderUsers()}
          {activeTab === "questions"  && renderQuestions()}
          {activeTab === "topics"     && renderTopics()}
          {activeTab === "analytics"  && renderAnalytics()}
          {activeTab === "challenges" && renderChallenges()}
        </motion.div>
      </main>
    </div>
  );
};

export default AdminPage;
